<template>
  <div>
    <!-- Loading State -->
    <LoadingState
      v-if="loading && !dgBillingRecords.length"
      type="card"
      :loading="true"
      message="Loading DG billing records..."
      sub-message="Please wait while we fetch your DG billing history"
    />

    <!-- Error State -->
    <ErrorState
      v-else-if="error && !dgBillingRecords.length"
      type="card"
      title="Failed to Load DG Billing Records"
      :message="error"
      details="Please check your connection and try again"
      error-type="network"
      :show-retry="true"
      :show-support="true"
      @retry="refreshData"
      @support="contactSupport"
    />

    <!-- Empty State -->
    <EmptyState
      v-else-if="!loading && !dgBillingRecords.length"
      title="No DG Billing Records Found"
      message="You haven't created any DG billing records yet."
      description="Start by creating your first diesel generator bill to see it appear here."
      icon="mdi-engine-outline"
      icon-color="primary"
      primary-action-text="Create First DG Bill"
      primary-action-icon="mdi-plus"
      :show-create="true"
      :show-refresh="true"
      @primary-action="createNewDGBill"
      @refresh="refreshData"
    />

    <!-- DG Billing List or Detail View -->
    <router-view
      v-else
      @show-message="showMessage"
    />

    <!-- Global Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      {{ snackbar.message }}
      <template #action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import LoadingState from '../components/common/LoadingState.vue';
import ErrorState from '../components/common/ErrorState.vue';
import EmptyState from '../components/common/EmptyState.vue';

export default {
  name: 'DGBillingHistory',

  components: {
    LoadingState,
    ErrorState,
    EmptyState,
  },

  data() {
    return {
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 5000,
      },
    };
  },

  computed: {
    ...mapGetters('dgBilling', ['allDGBillingRecords', 'loading', 'error']),

    dgBillingRecords() {
      return this.allDGBillingRecords || [];
    },
  },

  async created() {
    await this.refreshData();
  },

  methods: {
    ...mapActions('dgBilling', ['fetchDGBillingRecords']),

    async refreshData() {
      try {
        await this.fetchDGBillingRecords();

        // Show success message only if there were previous errors
        if (this.error) {
          this.showMessage({
            type: 'success',
            message: 'DG billing records refreshed successfully',
          });
        }
      } catch (error) {
        console.error('Error refreshing DG billing records:', error);

        // Provide user-friendly error messages
        let errorMessage = 'Failed to refresh DG billing records';

        if (error.message) {
          if (error.message.includes('network') || error.message.includes('connection')) {
            errorMessage = 'Network connection failed. Please check your internet connection and try again.';
          } else if (error.message.includes('timeout')) {
            errorMessage = 'Request timed out. Please try again or check your connection.';
          } else if (error.message.includes('permission') || error.message.includes('403')) {
            errorMessage = 'Access denied. Please contact your administrator for DG billing access.';
          } else if (error.message.includes('not found') || error.message.includes('404')) {
            errorMessage = 'DG billing service not available. Please contact support.';
          } else if (error.message.length < 100) {
            errorMessage = error.message;
          }
        }

        this.showMessage({
          type: 'error',
          message: errorMessage,
        });
      }
    },

    createNewDGBill() {
      this.$router.push('/dg-billing-entry');
    },

    contactSupport() {
      this.showMessage({
        type: 'info',
        message: 'Support contact information: support@shopworx.com',
      });
    },

    getSnackbarColor(type) {
      const colorByType = {
        error: 'error',
        warning: 'warning',
        info: 'info',
      };

      return colorByType[type] || 'success';
    },

    showMessage({ type, message }) {
      this.snackbar = {
        show: true,
        message,
        color: this.getSnackbarColor(type),
        timeout: type === 'error' ? 8000 : 5000,
      };
    },
  },
};
</script>
